import React, { useEffect, useState, useCallback, useMemo } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell
} from 'recharts';

const pdfFontsSource =
  pdfFonts?.pdfMake?.vfs ||
  pdfFonts?.default?.pdfMake?.vfs ||
  pdfFonts?.vfs ||
  pdfFonts?.default?.vfs ||
  (typeof pdfFonts === 'object' ? pdfFonts : null);

if (pdfFontsSource) {
  pdfMake.vfs = pdfFontsSource;
} else {
  // eslint-disable-next-line no-console
  console.warn('pdfmake fonts bundle was not found; generated PDFs may lack text.');
}

const STATUS = {
  loading: 'loading',
  unauthenticated: 'unauthenticated',
  authenticated: 'authenticated'
};

const REPORT_TIMEZONE = 'America/Chicago';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const PDF_PAGE_MARGIN = 20;

const CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);

function BookingsXAxisTick({ x, y, payload, aggregatedData }) {
  const label = payload?.value || '';
  const index = typeof payload?.index === 'number' ? payload.index : null;
  const bucketByIndex = index !== null ? aggregatedData?.[index] : null;
  const bucketByLabel =
    !bucketByIndex && label
      ? aggregatedData?.find((item) => item.label === label)
      : null;
  const bucket = bucketByIndex || bucketByLabel;
  const totalAmount = bucket ? bucket.totalAmount : null;
  const formattedTotal = formatCurrencyValue(totalAmount);
  const totalLabel =
    formattedTotal && formattedTotal !== '—' ? formattedTotal : '';

  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={-2} textAnchor="middle" fill="#0f172a" fontSize={12}>
        {label}
      </text>
      {totalLabel ? (
        <text dy={12} textAnchor="middle" fill="#4b5563" fontSize={11}>
          {totalLabel}
        </text>
      ) : null}
    </g>
  );
}

function parseNumberValue(value) {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '');
    const match = cleaned.match(/-?\d+(?:\.\d+)?/);
    if (!match) return 0;
    const numeric = Number(match[0]);
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
}

const TOTAL_VALUE_PATHS = [
  'metadata.total',
  'metadata.total_amount',
  'metadata.totalAmount',
  'metadata.totalFee',
  'metadata.total_price',
  'metadata.amount',
  'metadata.price',
  'metadata.amountTotal',
  'metadata.totalDue',
  'responses.total',
  'responses.total_amount',
  'responses.totalAmount',
  'responses.totalFee',
  'responses.total_price',
  'responses.amount',
  'responses.price',
  'responses.amountTotal',
  'responses.totalDue',
  'total',
  'total_amount',
  'totalAmount',
  'totalFee',
  'total_price',
  'amount',
  'price',
  'amountTotal',
  'totalDue'
];

function getBookingTotalInfo(booking) {
  let fallback = null;

  for (const path of TOTAL_VALUE_PATHS) {
    const value = getValueByPath(booking, path);
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && !value.trim()) continue;

    const parsed = parseNumberValue(value);
    const info = { path, value, parsed };

    if (parsed > 0) return info;
    if (!fallback) fallback = info;
  }

  return fallback || { path: '', value: undefined, parsed: 0 };
}

function extractBookingTotalAmount(booking) {
  return getBookingTotalInfo(booking).parsed;
}

function extractBookingStatus(booking) {
  const status =
    booking?.status ||
    booking?.bookingStatus ||
    booking?.state ||
    booking?.status_label ||
    booking?.statusLabel ||
    booking?.metadata?.status ||
    '';
  return typeof status === 'string' ? status.trim() : '';
}

function isBookingCancelled(booking) {
  const status = extractBookingStatus(booking);
  return Boolean(status && CANCELLED_STATUSES.has(status.toLowerCase()));
}

const DISPLAY_STATUSES = new Set(['confirmed', 'completed', 'cancelled', 'canceled']);

export default function WeeklyBookings() {
  const [status, setStatus] = useState(STATUS.loading);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState('');
  const [groupMode, setGroupMode] = useState('day');
  const [selectedBucketKey, setSelectedBucketKey] = useState('');
  const [authToken, setAuthToken] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      return window.sessionStorage.getItem('weeklyAuthToken') || '';
    } catch {
      return '';
    }
  });

  const apiBase = useMemo(() => {
    const explicit =
      process.env.REACT_APP_WEEKLY_API_BASE || process.env.REACT_APP_API_URL || '';
    if (explicit) {
      return explicit.endsWith('/') ? explicit.slice(0, -1) : explicit;
    }

    if (typeof window !== 'undefined') {
      const { protocol, hostname, port } = window.location;
      if (port && port !== '80' && port !== '443') {
        if (port === '3000') {
          return `${protocol}//${hostname}:5000`;
        }
      }
    }

    return '';
  }, []);

  const performFetch = useCallback(
    (path, options = {}, includeAuth = true) => {
      const config = { ...options };
      const headers = new Headers(config.headers || {});
      if (includeAuth && authToken) {
        headers.set('X-Weekly-Auth', authToken);
      }
      config.headers = headers;
      config.credentials = 'include';
      return fetch(`${apiBase}${path}`, config);
    },
    [apiBase, authToken]
  );

  const apiFetch = useCallback(
    (path, options = {}) => performFetch(path, options, true),
    [performFetch]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (authToken) {
        window.sessionStorage.setItem('weeklyAuthToken', authToken);
      } else {
        window.sessionStorage.removeItem('weeklyAuthToken');
      }
    } catch {
      // ignore storage errors
    }
  }, [authToken]);

  const fetchSession = useCallback(async () => {
    try {
      const response = await apiFetch('/api/weekly-bookings/session');

      if (response.ok) {
        setStatus(STATUS.authenticated);
      } else {
        if (response.status === 401 && authToken) {
          setAuthToken('');
        }
        setStatus(STATUS.unauthenticated);
      }
    } catch (err) {
      console.error('Failed to check weekly bookings session', err);
      setStatus(STATUS.unauthenticated);
    }
  }, [apiFetch, authToken]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const fetchBookings = useCallback(async (logTotals = false) => {
    setBookingsLoading(true);
    setBookingsError('');
    try {
      const response = await apiFetch('/api/weekly-bookings/bookings');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) {
          setAuthToken('');
          setStatus(STATUS.unauthenticated);
        }
        throw new Error(payload?.error || 'Unable to load bookings.');
      }
      const rawList = Array.isArray(payload?.bookings) ? payload.bookings : [];
      const list = rawList.filter((booking) => {
        const status = extractBookingStatus(booking);
        if (!status) return false;
        const normalized = status.toLowerCase();
        return DISPLAY_STATUSES.has(normalized);
      });
      if (logTotals) {
        const totals = list.map((booking) => {
          const info = getBookingTotalInfo(booking);
          return {
            id:
              booking?.id ||
              booking?.uid ||
              booking?.booking_id ||
              booking?.metadata?.booking_id ||
              null,
            field: info.path,
            raw: info.value ?? null,
            parsed: info.parsed
          };
        });
        console.log('Weekly bookings totals:', totals);
      }
      setBookings(list);
    } catch (err) {
      console.error('Failed to load bookings', err);
      setBookingsError(err?.message || 'Unable to load bookings.');
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (status === STATUS.authenticated) {
      fetchBookings();
    } else {
      setBookings([]);
    }
  }, [status, fetchBookings]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await performFetch('/api/weekly-bookings/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      }, false);

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        setError(payload.error || 'Login failed. Please try again.');
        setSubmitting(false);
        return;
      }

      const payload = await response.json().catch(() => ({}));
      if (payload?.token) {
        setAuthToken(payload.token);
      }

      setStatus(STATUS.authenticated);
      setUsername('');
      setPassword('');
      setSubmitting(false);
    } catch (err) {
      console.error('Weekly bookings login failed', err);
      setError('Unable to sign in right now. Please try again.');
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiFetch('/api/weekly-bookings/logout', {
        method: 'POST'
      });
    } catch (err) {
      console.warn('Failed to log out weekly bookings session', err);
    } finally {
      setStatus(STATUS.unauthenticated);
      setBookings([]);
      setError('');
      setSubmitting(false);
      setUsername('');
      setPassword('');
      setAuthToken('');
    }
  };

  const aggregatedData = useMemo(
    () => aggregateBookings(bookings, groupMode),
    [bookings, groupMode]
  );

  const groupOptions = useMemo(
    () => [
      { value: 'day', label: 'Day' },
      { value: 'week', label: 'Week' },
      { value: 'month', label: 'Month' }
    ],
    []
  );

  useEffect(() => {
    if (!aggregatedData.length) {
      setSelectedBucketKey('');
      return;
    }
    const stillExists = aggregatedData.some((item) => item.key === selectedBucketKey);
    if (!stillExists) {
      setSelectedBucketKey(aggregatedData[aggregatedData.length - 1].key);
    }
  }, [aggregatedData, selectedBucketKey]);

  const selectedBucket = useMemo(
    () => aggregatedData.find((item) => item.key === selectedBucketKey) || null,
    [aggregatedData, selectedBucketKey]
  );

  const handleDownloadPdf = useCallback(() => {
    if (!selectedBucket || !selectedBucket.bookings.length) return;

    try {
      const docDefinition = buildWeeklyBookingsPdf(
        selectedBucket,
        groupMode,
        REPORT_TIMEZONE
      );
      const downloadName = `weekly-bookings-${groupMode}-${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`;
      pdfMake.createPdf(docDefinition).download(downloadName);
    } catch (error) {
      console.error('Failed to generate weekly bookings PDF', error);
    }
  }, [groupMode, selectedBucket]);

  if (status === STATUS.loading) {
    return (
      <main className="page-shell auth-shell">
        <div className="card section-panel panel-cream auth-card">
          <h1 className="section-heading" style={{ textAlign: 'center' }}>
            Loading portal…
          </h1>
        </div>
      </main>
    );
  }

  if (status === STATUS.authenticated) {
    return (
      <main className="page-shell auth-shell">
        <div className="card section-panel panel-cream auth-card">
          <header className="auth-header">
            <h1 className="section-heading" style={{ margin: 0 }}>
              Weekly
            </h1>
            <button type="button" className="auth-link" onClick={handleLogout}>
              Log out
            </button>
          </header>

          <p className="auth-subtitle">
            You’re signed in. Review current bookings and export the summary for reporting.
          </p>

          <div className="bookings-panel">
            <div className="bookings-header">
              <div className="bookings-meta">
                <div className="bookings-meta-top">
                  <span className="bookings-total">
                    Total bookings loaded: {bookings.length}
                  </span>
                  <button
                    type="button"
                    className="auth-link button-like bookings-refresh"
                    onClick={() => fetchBookings(true)}
                    disabled={bookingsLoading}
                  >
                    Refresh
                  </button>
                </div>
                {(bookingsLoading || bookingsError) && (
                  <div className="bookings-meta-status">
                    {bookingsLoading && <span className="bookings-hint">Refreshing…</span>}
                    {bookingsError && (
                      <span className="bookings-error" role="alert">
                        {bookingsError}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="bookings-actions">
                <span className="bookings-group__label">Group by</span>
                <div className="bookings-actions__controls">
                  <div className="bookings-group__toggle" role="group" aria-label="Group bookings">
                    {groupOptions.map((option) => {
                      const isActive = groupMode === option.value;
                      return (
                        <button
                          type="button"
                          key={option.value}
                          className={`bookings-group__button${
                            isActive ? ' bookings-group__button--active' : ''
                          }`}
                          aria-pressed={isActive}
                          onClick={() => setGroupMode(option.value)}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="auth-link button-like bookings-download"
                    onClick={handleDownloadPdf}
                    disabled={!selectedBucket || !selectedBucket.bookings.length}
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            </div>

            <div className="bookings-chart">
              {bookingsLoading ? (
                <div className="bookings-empty">Loading bookings…</div>
              ) : bookingsError ? (
                <div className="bookings-empty" role="alert">
                  Could not load bookings. Try refreshing.
                </div>
              ) : aggregatedData.length ? (
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart
                    data={aggregatedData}
                    margin={{ top: 8, right: 20, left: 0, bottom: 8 }}
                    onClick={() => {}}
                  >
                    <defs>
                      <linearGradient id="cancelledBookingsGradient" x1="0" y1="1" x2="1" y2="0">
                        <stop offset="0%" stopColor="#d94b4bf2" />
                        <stop offset="100%" stopColor="#b94242e6" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" stroke="rgba(17,95,99,0.15)" />
                    <XAxis
                      dataKey="label"
                      interval={0}
                      tick={<BookingsXAxisTick aggregatedData={aggregatedData} />}
                      tickMargin={12}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip
                      cursor={{ fill: 'rgba(17,95,99,0.1)' }}
                      formatter={(value, name) => {
                        const countLabel = value === 1 ? 'booking' : 'bookings';
                        if (name === 'cancelledCount') {
                          return [`${value} ${countLabel}`, 'Cancelled'];
                        }
                        if (name === 'activeCount') {
                          return [`${value} ${countLabel}`, 'Active'];
                        }
                        return [`${value} ${countLabel}`, 'Bookings'];
                      }}
                    />
                    <Bar
                      dataKey="activeCount"
                      stackId="bookings"
                      fill="#1E6B67"
                      onClick={(data) => {
                        const key = data?.payload?.key;
                        if (key) setSelectedBucketKey(key);
                      }}
                    >
                      {aggregatedData.map((entry) => {
                        const isSelected = entry.key === selectedBucketKey;
                        return (
                          <Cell
                            key={`active-${entry.key}`}
                            radius={
                              entry.cancelledCount > 0 ? [0, 0, 0, 0] : [8, 8, 0, 0]
                            }
                            fillOpacity={isSelected ? 1 : 0.45}
                          />
                        );
                      })}
                    </Bar>
                    <Bar
                      dataKey="cancelledCount"
                      stackId="bookings"
                      fill="url(#cancelledBookingsGradient)"
                      onClick={(data) => {
                        const key = data?.payload?.key;
                        if (key) setSelectedBucketKey(key);
                      }}
                    >
                      {aggregatedData.map((entry) => {
                        const isSelected = entry.key === selectedBucketKey;
                        return (
                          <Cell
                            key={`cancelled-${entry.key}`}
                            radius={
                              entry.cancelledCount > 0 ? [8, 8, 0, 0] : [0, 0, 0, 0]
                            }
                            fillOpacity={isSelected ? 1 : 0.45}
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="bookings-empty">No bookings available for the selected range.</div>
              )}
            </div>

            <BookingsDetailPanel bucket={selectedBucket} timezone="America/Chicago" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell auth-shell">
      <div className="card section-panel panel-cream auth-card">
        <h1 className="section-heading" style={{ textAlign: 'center' }}>
          Weekly Portal
        </h1>
        <p className="auth-subtitle">
          Enter the credentials provided to your team to view weekly bookings.
        </p>

        <form className="auth-form" onSubmit={handleSubmit}>
          <label className="auth-label" htmlFor="weekly-username">
            Login
            <input
              id="weekly-username"
              name="username"
              autoComplete="username"
              type="text"
              value={username}
              disabled={submitting}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </label>

          <label className="auth-label" htmlFor="weekly-password">
            Password
            <input
              id="weekly-password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              disabled={submitting}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>

          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </main>
  );
}

const dayFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric'
});

const monthFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  year: 'numeric'
});

function getWeekInfoInTimezone(date, timezone) {
  const parts = getDateParts(date, timezone);
  if (!parts.year || !parts.month || !parts.day) return null;

  // Anchor to midday to avoid DST edge cases when shifting days
  const localMidday = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
  const dayOfWeek = localMidday.getUTCDay(); // Sunday = 0 ... Saturday = 6

  const weekStart = new Date(localMidday);
  weekStart.setUTCDate(weekStart.getUTCDate() - dayOfWeek);
  const weekEnd = new Date(weekStart);
  weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);

  const monthStart = new Date(Date.UTC(parts.year, parts.month - 1, 1, 12, 0, 0));
  const monthEnd = new Date(Date.UTC(parts.year, parts.month, 0, 12, 0, 0));

  const clampedStart = weekStart < monthStart ? monthStart : weekStart;
  const clampedEnd = weekEnd > monthEnd ? monthEnd : weekEnd;

  const firstWeekStart = new Date(monthStart);
  firstWeekStart.setUTCDate(firstWeekStart.getUTCDate() - firstWeekStart.getUTCDay());

  const weekIndexInMonth =
    Math.floor((weekStart.getTime() - firstWeekStart.getTime()) / (7 * MS_PER_DAY)) + 1;

  const sortKey = `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(
    weekIndexInMonth
  ).padStart(2, '0')}`;

  const rangeLabel = deriveDateRangeLabel(clampedStart, clampedEnd, timezone);
  const label = rangeLabel;

  return {
    key: sortKey,
    label,
    sortKey,
    rangeLabel
  };
}

function aggregateBookings(bookings, mode) {
  if (!Array.isArray(bookings) || !bookings.length) return [];

  const buckets = new Map();

  bookings.forEach((booking) => {
    const start =
      booking.startTime ||
      booking.start_time ||
      booking.start ||
      booking.start_at ||
      booking.startsAt;

    const bookingTotal = extractBookingTotalAmount(booking);

    if (!start) return;
    const date = new Date(start);
    if (Number.isNaN(date.valueOf())) return;

    if (mode === 'week') {
      const weekInfo = getWeekInfoInTimezone(date, REPORT_TIMEZONE);
      if (!weekInfo) return;
      const { key, label, sortKey, rangeLabel } = weekInfo;
      const current = buckets.get(key) || {
        key,
        label,
        count: 0,
        sortKey,
        bookings: [],
        rangeLabel,
        cancelledCount: 0,
        activeCount: 0,
        totalAmount: 0
      };
      current.count += 1;
      if (isBookingCancelled(booking)) {
        current.cancelledCount += 1;
      } else {
        current.activeCount += 1;
      }
      current.totalAmount += bookingTotal;
      current.bookings.push(booking);
      buckets.set(key, current);
      return;
    }

    if (mode === 'month') {
      const { sortKey, label, rangeLabel } = getMonthInfo(date);
      const current = buckets.get(sortKey) || {
        key: sortKey,
        label,
        count: 0,
        sortKey,
        bookings: [],
        rangeLabel,
        cancelledCount: 0,
        activeCount: 0,
        totalAmount: 0
      };
      current.count += 1;
      if (isBookingCancelled(booking)) {
        current.cancelledCount += 1;
      } else {
        current.activeCount += 1;
      }
      current.totalAmount += bookingTotal;
      current.bookings.push(booking);
      buckets.set(sortKey, current);
      return;
    }

    const isoDay = date.toISOString().slice(0, 10);
    const current = buckets.get(isoDay) || {
      key: isoDay,
      label: dayFormatter.format(date),
      count: 0,
      sortKey: isoDay,
      bookings: [],
      cancelledCount: 0,
      activeCount: 0,
      totalAmount: 0
    };
    current.count += 1;
    if (isBookingCancelled(booking)) {
      current.cancelledCount += 1;
    } else {
      current.activeCount += 1;
    }
    current.totalAmount += bookingTotal;
    current.bookings.push(booking);
    buckets.set(isoDay, current);
  });

  return Array.from(buckets.values()).sort((a, b) =>
    a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0
  );
}

function getMonthInfo(date) {
  const year = date.getUTCFullYear();
  const monthIndex = date.getUTCMonth();
  const sortKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
  const label = monthFormatter.format(date);
  const monthStart = new Date(Date.UTC(year, monthIndex, 1));
  const monthEnd = new Date(Date.UTC(year, monthIndex + 1, 0, 23, 59, 59, 999));
  const rangeLabel = deriveDateRangeLabel(monthStart, monthEnd, REPORT_TIMEZONE);
  return { year, month: monthIndex + 1, sortKey, label, rangeLabel };
}

function BookingsDetailPanel({ bucket, timezone }) {
  if (!bucket) {
    return (
      <div className="bookings-details">
        <p className="bookings-empty">Select a bar to view booking details.</p>
      </div>
    );
  }

  if (!bucket.bookings.length) {
    return (
      <div className="bookings-details">
        <p className="bookings-empty">No bookings recorded for {bucket.label}.</p>
      </div>
    );
  }

  return (
    <div className="bookings-details">
      <h2 className="bookings-details__title">Bookings for {bucket.label}</h2>
      <div className="bookings-details__list">
        {bucket.bookings.map((booking) => (
          <BookingCard key={booking.id || booking.uid} booking={booking} timezone={timezone} />
        ))}
      </div>
    </div>
  );
}

function BookingCard({ booking, timezone }) {
  const attendee = booking.attendees?.[0] || {};
  const responses = booking.responses || {};
  const metadata = booking.metadata || {};

  const HIDDEN_METADATA_KEYS = new Set([
    'source',
    'bookingid',
    'booking_id',
    'service',
    'service_type',
    'servicetype'
  ]);

  const startValue = extractFirstValue(
    [booking],
    ['startTime', 'start_time', 'start', 'start_at', 'startsAt']
  );
  const endValue = extractFirstValue(
    [booking],
    ['endTime', 'end_time', 'end', 'end_at', 'endsAt']
  );

  const statusLabel = extractBookingStatus(booking) || 'Unknown';
  const isCancelled = isBookingCancelled(booking);

  const name =
    `${responses?.name?.firstName || ''} ${responses?.name?.lastName || ''}`.trim() ||
    attendee.name ||
    responses.name ||
    'Booking';

  const phone =
    typeof responses.attendeePhoneNumber === 'string' && responses.attendeePhoneNumber.trim()
      ? responses.attendeePhoneNumber
      : typeof metadata.phone === 'string' && metadata.phone.trim()
      ? metadata.phone
      : 'Not provided';

  const rawAddress =
    typeof responses.address === 'string' && responses.address.trim()
      ? responses.address
      : typeof metadata.address === 'string' && metadata.address.trim()
      ? metadata.address
      : typeof booking.address === 'string' && booking.address.trim()
      ? booking.address
      : 'Not provided';
  const address = normalizeBasicValue(rawAddress) || 'Not provided';
  const addressLink = buildMapsPlaceLink(address);

  const arrivalWindow = formatArrivalWindow(startValue, endValue, timezone);

  const requestsValue =
    metadata.requests || responses.requests || booking.requests || '';
  const formattedRequests = formatRequestsValue(requestsValue);

  const rawTotal = getBookingTotalInfo(booking).value;
  const formattedTotal =
    rawTotal !== null && rawTotal !== undefined && typeof rawTotal !== 'object' && !Array.isArray(rawTotal)
      ? formatCurrencyValue(rawTotal)
      : '—';

  const metadataEntries = Object.entries(metadata).filter(([key, value]) => {
    if (!key || value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return false;
    if (typeof value === 'object') return false;
    const normalizedKey = key.toString().trim().toLowerCase();
    if (HIDDEN_METADATA_KEYS.has(normalizedKey)) return false;
    if (normalizedKey === 'requests') return false;
    if (normalizedKey === 'total' || normalizedKey === 'total_amount' || normalizedKey === 'totalamount') return false;
    return true;
  });

  const displayEntries = [...metadataEntries];
  if (formattedRequests && formattedRequests !== '—') {
    displayEntries.push(['Requests', formattedRequests]);
  }
  if (
    rawTotal !== undefined &&
    rawTotal !== null &&
    typeof rawTotal !== 'object' &&
    !Array.isArray(rawTotal) &&
    formattedTotal &&
    formattedTotal !== '—'
  ) {
    displayEntries.push(['Total', formattedTotal]);
  }

  return (
    <article className={`booking-card${isCancelled ? ' booking-card--cancelled' : ''}`}>
      <header className="booking-card__header">
        <h3>{name}</h3>
        <span
          className={`booking-card__status${
            isCancelled ? ' booking-card__status--cancelled' : ''
          }`}
        >
          {statusLabel}
        </span>
      </header>

      <dl className="booking-card__grid">
        <div>
          <dt>Phone</dt>
          <dd>{phone}</dd>
        </div>
        <div>
          <dt>Address</dt>
          <dd>
            {addressLink ? (
              <a href={addressLink} target="_blank" rel="noreferrer noopener">
                {address}
              </a>
            ) : (
              address
            )}
          </dd>
        </div>
        <div>
          <dt>Arriving time</dt>
          <dd>{arrivalWindow}</dd>
        </div>
      </dl>

      {displayEntries.length > 0 && (
        <div className="booking-card__metadata">
          <h4>Metadata</h4>
          <ul>
            {displayEntries.map(([key, value]) => {
              const label = key === 'Requests' || key === 'Total' ? key : prettifyKey(key);
              return (
                <li key={label}>
                  <span className="meta-key">{label}</span>
                  <span className="meta-value">{String(value)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </article>
  );
}

function formatArrivalWindow(startValue, endValue, timezone = 'America/Chicago') {
  const startDate = parseDateValue(startValue);
  const endDate = parseDateValue(endValue);
  if (!startDate) return 'Not provided';

  const dateLabel = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    month: 'short',
    day: 'numeric'
  }).format(startDate);

  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const startTimeLabel = timeFormatter.format(startDate);
  const endTimeLabel = endDate ? timeFormatter.format(endDate) : '';

  const stripMeridiem = (label) => label.replace(/\s?(AM|PM)$/i, '');

  let rangeLabel = startTimeLabel;
  if (endTimeLabel) {
    const startMeridiemMatch = startTimeLabel.match(/\b(AM|PM)$/i);
    const endMeridiemMatch = endTimeLabel.match(/\b(AM|PM)$/i);
    const sameMeridiem =
      startMeridiemMatch && endMeridiemMatch && startMeridiemMatch[0] === endMeridiemMatch[0];
    if (sameMeridiem) {
      rangeLabel = `${stripMeridiem(startTimeLabel)}-${endTimeLabel}`;
    } else {
      rangeLabel = `${startTimeLabel}-${endTimeLabel}`;
    }
  }

  return `${dateLabel}, ${rangeLabel}`;
}

function prettifyKey(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

function buildWeeklyBookingsPdf(bucket, groupMode, timezone) {
  const dayGroups = groupBookingsByDayForPdf(bucket.bookings, timezone);
  const { title, subtitle, documentTitle } = buildPdfHeader(bucket, groupMode, timezone);
  const { acceptedTotal, cancelledTotal } = computeBookingTotals(bucket.bookings);

  const content = [
    {
      text: title,
      style: 'pdfTitle',
      margin: [0, 0, 0, subtitle ? 4 : 16]
    }
  ];

  if (subtitle) {
    content.push({
      text: subtitle,
      style: 'pdfSubtitle',
      margin: [0, 0, 0, 16]
    });
  }

  content.push(createPdfLegend(acceptedTotal, cancelledTotal));

  if (dayGroups.length) {
    dayGroups.forEach((group, index) => {
      content.push(buildPdfDayTable(group, index === 0));
    });
  } else {
    content.push({
      text: 'No bookings available for the selected period.',
      style: 'pdfEmptyState',
      margin: [0, 24, 0, 0]
    });
  }

  return {
    info: {
      title: documentTitle
    },
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [PDF_PAGE_MARGIN, 48, PDF_PAGE_MARGIN, 48],
    content,
    defaultStyle: {
      fontSize: 10,
      color: '#1f2933'
    },
    styles: {
      pdfTitle: {
        fontSize: 18,
        bold: true,
        color: '#172b4d'
      },
      pdfSubtitle: {
        fontSize: 12,
        color: '#4b5563'
      },
      pdfLegendCell: {
        fontSize: 10,
        bold: true,
        color: '#1f2933',
        alignment: 'center'
      },
      pdfLegendAmount: {
        fontSize: 9,
        color: '#1f2933',
        alignment: 'center'
      },
      pdfSectionHeading: {
        fontSize: 14,
        bold: true,
        color: '#172b4d'
      },
      pdfTableHeader: {
        fontSize: 10,
        bold: true,
        color: '#1f2933'
      },
      pdfBodyCell: {
        fontSize: 9,
        color: '#1f2933',
        lineHeight: 1.25
      },
      pdfCancelledCell: {
        fontSize: 9,
        color: '#4b5563',
        decoration: 'lineThrough',
        lineHeight: 1.25
      },
      pdfAddressCell: {
        color: '#0b66c3',
        decoration: 'underline'
      },
      pdfRequestsCell: {
        fontSize: 9,
        color: '#1f2933'
      },
      pdfEmptyState: {
        fontSize: 11,
        italics: true,
        color: '#6b7280'
      }
    }
  };
}

function createPdfLegend(acceptedTotal, cancelledTotal) {
  const acceptedLabel = formatCurrencyValue(acceptedTotal);
  const cancelledLabel = formatCurrencyValue(cancelledTotal);
  return {
    columns: [
      { width: '*', text: '' },
      {
        width: 'auto',
        table: {
          body: [
            [
              {
                stack: [
                  { text: 'Accepted', style: 'pdfLegendCell' },
                  { text: acceptedLabel, style: 'pdfLegendAmount' }
                ],
                fillColor: '#e7f4e7',
                alignment: 'center'
              },
              {
                stack: [
                  { text: 'Cancelled', style: 'pdfLegendCell' },
                  { text: cancelledLabel, style: 'pdfLegendAmount' }
                ],
                fillColor: '#fbe1e4',
                alignment: 'center'
              }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 12,
          paddingRight: () => 12,
          paddingTop: () => 4,
          paddingBottom: () => 4
        }
      }
    ],
    columnGap: 12,
    margin: [0, 0, 0, 12]
  };
}

function buildPdfDayTable(group, isFirstGroup) {
  const { title, rows } = group;
  const dayHeadingRow = [
    {
      text: title,
      style: 'pdfSectionHeading',
      colSpan: 10,
      alignment: 'left',
      margin: [0, 4, 0, 4]
    },
    ...Array(9).fill('')
  ];
  const headerRow = [
    buildPdfHeaderCell('Total'),
    buildPdfHeaderCell('Time'),
    buildPdfHeaderCell('Address'),
    buildPdfHeaderCell('Client'),
    buildPdfHeaderCell('Service'),
    buildPdfHeaderCell('Size(sq.ft)'),
    buildPdfHeaderCell('Baths'),
    buildPdfHeaderCell('Rooms'),
    buildPdfHeaderCell('Extras'),
    buildPdfHeaderCell('Requests')
  ];

  const bodyRows = rows.map((row) => {
    const addressOverrides =
      row.address && typeof row.address === 'object' ? { style: ['pdfAddressCell'] } : {};
    return [
      buildPdfBodyCell(row.total, row, { alignment: 'right', noWrap: true }),
      buildPdfBodyCell(row.time, row),
      buildPdfBodyCell(row.address, row, addressOverrides),
      buildPdfBodyCell(row.client, row),
      buildPdfBodyCell(row.service, row),
      buildPdfBodyCell(row.size, row),
      buildPdfBodyCell(row.baths, row, { alignment: 'center' }),
      buildPdfBodyCell(row.rooms, row, { alignment: 'center' }),
      buildPdfBodyCell(row.extras, row),
      buildPdfBodyCell(row.requests, row, { style: ['pdfRequestsCell'], alignment: 'left' })
    ];
  });

  const tableDefinition = {
    table: {
      headerRows: 2,
      widths: [60, 78, 130, 56, 38, 48, 26, 30, 64, 76],
      body: [dayHeadingRow, headerRow, ...bodyRows],
      dontBreakRows: true,
      keepWithHeaderRows: 2
    },
    layout: {
      hLineWidth: (index, node) => {
        if (index === 0 || index === node.table.body.length) return 0.5;
        return 0.25;
      },
      vLineWidth: () => 0.25,
      hLineColor: () => '#c4c4c4',
      vLineColor: () => '#c4c4c4',
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: () => 6,
      paddingBottom: () => 6
    }
  };

  return {
    columns: [
      { width: '*', text: '' },
      {
        width: 'auto',
        ...tableDefinition,
        margin: [0, isFirstGroup ? 24 : 28, 0, 12]
      },
      { width: '*', text: '' }
    ],
    columnGap: 12
  };
}

function buildPdfHeaderCell(text) {
  return {
    text,
    style: 'pdfTableHeader',
    fillColor: '#d9d9d9',
    margin: [0, 2, 0, 2]
  };
}

function buildPdfBodyCell(value, row, overrides = {}) {
  const isObjectValue = value && typeof value === 'object' && !Array.isArray(value);
  const baseCell = isObjectValue ? { ...value } : { text: formatDisplayValue(value) };
  const { style, fillColor: overrideFill, ...restOverrides } = overrides;
  const extraStyles = Array.isArray(style)
    ? style.filter(Boolean)
    : style
    ? [style]
    : [];
  extraStyles.unshift(row.isCancelled ? 'pdfCancelledCell' : 'pdfBodyCell');
  const fillColor = overrideFill ?? (row.isCancelled ? '#fbe1e4' : '#e7f4e7');

  return {
    ...baseCell,
    ...restOverrides,
    style: extraStyles,
    fillColor
  };
}

function groupBookingsByDayForPdf(bookings, timezone) {
  const groups = new Map();

  bookings.forEach((booking) => {
    const startValue = extractFirstValue(
      [booking],
      ['startTime', 'start_time', 'start', 'start_at', 'startsAt']
    );
    const startDate = parseDateValue(startValue);
    if (!startDate) return;

    const isoDay = startDate.toISOString().slice(0, 10);
    const title = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      month: 'short',
      day: 'numeric'
    }).format(startDate);

    const row = buildPdfRow(booking, timezone);
    if (!row) return;

    if (!groups.has(isoDay)) {
      groups.set(isoDay, { key: isoDay, title, rows: [row] });
    } else {
      groups.get(isoDay).rows.push(row);
    }
  });

  const sortedGroups = Array.from(groups.values()).sort((a, b) =>
    a.key < b.key ? -1 : a.key > b.key ? 1 : 0
  );

  sortedGroups.forEach((group) => {
    group.rows.sort((a, b) => a.sortKey - b.sortKey);
  });

  return sortedGroups;
}

function buildPdfRow(booking, timezone) {
  const attendee = booking.attendees?.[0] || {};
  const responses = booking.responses || {};
  const metadata = booking.metadata || {};

  const attendeeName =
    `${responses?.name?.firstName || ''} ${responses?.name?.lastName || ''}`.trim() ||
    attendee.name ||
    responses.name ||
    extractFirstValue([metadata], ['clientName', 'customerName']) ||
    '';

  const addressValue = extractFirstValue(
    [responses, metadata, booking, booking.location],
    ['address', 'location.address', 'addressLine1', 'fullAddress']
  );

  const startValue = extractFirstValue(
    [booking],
    ['startTime', 'start_time', 'start', 'start_at', 'startsAt']
  );
  const endValue = extractFirstValue(
    [booking],
    ['endTime', 'end_time', 'end', 'end_at', 'endsAt']
  );

  const totalValue = getBookingTotalInfo(booking).value;

  const serviceValue = extractFirstValue(
    [metadata, responses, booking],
    ['service', 'serviceType', 'service_type', 'eventType', 'event_type']
  );

  const sizeValue = extractFirstValue(
    [metadata, responses],
    ['size', 'squareFeet', 'square_feet', 'squarefeet', 'sqft']
  );

  const bathsValue = extractFirstValue(
    [metadata, responses],
    ['baths', 'bathrooms', 'bathroomCount']
  );

  const roomsValue = extractFirstValue(
    [metadata, responses],
    ['rooms', 'roomCount', 'bedrooms']
  );

  const extrasValue = extractFirstValue(
    [metadata, responses],
    ['extras', 'extraServices', 'extra_services', 'addOns', 'addons']
  );

  const requestsValue = extractFirstValue(
    [metadata, responses, booking],
    ['requests', 'specialRequests', 'notes', 'comments']
  );

  const addressText = normalizeBasicValue(addressValue);
  const addressLink = buildMapsPlaceLink(addressText);
  const address = addressText
    ? {
        text: addressText,
        link: addressLink || undefined
      }
    : '—';

  const startDate = parseDateValue(startValue);

  return {
    sortKey: startDate ? startDate.getTime() : 0,
    isCancelled: isBookingCancelled(booking),
    total: formatCurrencyValue(totalValue),
    time: formatTimeRange(startValue, endValue, timezone),
    address,
    client: attendeeName || '—',
    service: formatDisplayValue(serviceValue),
    size: formatDisplayValue(formatSquareFeetValue(sizeValue)),
    baths: formatDisplayValue(normalizeBasicValue(bathsValue)),
    rooms: formatDisplayValue(formatRoomsValue(roomsValue)),
    extras: formatDisplayValue(formatExtrasValue(extrasValue)),
    requests: formatDisplayValue(formatRequestsValue(requestsValue))
  };
}

function computeBookingTotals(bookings = []) {
  let acceptedTotal = 0;
  let cancelledTotal = 0;

  bookings.forEach((booking) => {
    const amount = extractBookingTotalAmount(booking);
    if (isBookingCancelled(booking)) {
      cancelledTotal += amount;
    } else {
      acceptedTotal += amount;
    }
  });

  return { acceptedTotal, cancelledTotal };
}

function formatTimeRange(startValue, endValue, timezone) {
  const startDate = parseDateValue(startValue);
  const endDate = parseDateValue(endValue);
  if (!startDate && !endDate) return '—';

  const formatter = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit'
  });

  if (startDate && endDate) {
    return `${formatter.format(startDate)} – ${formatter.format(endDate)}`;
  }

  return formatter.format(startDate || endDate);
}

function parseDateValue(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? null : date;
}

function extractFirstValue(sources, keys) {
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const value = getValueByPath(source, key);
      if (value === undefined || value === null) continue;
      if (typeof value === 'string') {
        if (!value.trim()) continue;
        return value;
      }
      if (Array.isArray(value) && value.length === 0) {
        continue;
      }
      return value;
    }
  }
  return '';
}

function getValueByPath(source, path) {
  if (!source || !path) return undefined;
  if (path.includes('.')) {
    return path.split('.').reduce((acc, segment) => {
      if (acc && Object.prototype.hasOwnProperty.call(acc, segment)) {
        return acc[segment];
      }
      return undefined;
    }, source);
  }
  return source[path];
}

function normalizeBasicValue(value) {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || /^not provided$/i.test(trimmed) || /^n\/a$/i.test(trimmed)) {
      return '';
    }
    return trimmed;
  }
  return value;
}

function formatCurrencyValue(value) {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }
  const stringValue = String(value).trim();
  if (!stringValue) return '—';

  const numeric = Number(stringValue.replace(/[^0-9.-]/g, ''));
  if (Number.isFinite(numeric)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numeric);
  }

  return stringValue;
}

function formatSquareFeetValue(value) {
  const normalized = normalizeBasicValue(value);
  if (!normalized) return '';
  if (typeof normalized !== 'string' && typeof normalized !== 'number') {
    return '';
  }
  const stringValue = typeof normalized === 'string' ? normalized : String(normalized);
  const stripped = stringValue.replace(/sq\s*\.?\s*ft/gi, '').replace(/\s+/g, ' ').trim();
  const numeric = Number(stripped.replace(/[^0-9.]/g, ''));
  if (Number.isFinite(numeric) && numeric > 0) {
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 0
    }).format(Math.round(numeric));
  }
  return stripped;
}

function formatRoomsValue(value) {
  const normalized = normalizeBasicValue(value);
  if (!normalized) return '';
  const numeric = Number(normalized);
  if (Number.isFinite(numeric)) {
    return String(numeric);
  }
  return normalized;
}

function formatExtrasValue(value) {
  if (Array.isArray(value)) {
    const cleaned = value.map((item) => normalizeBasicValue(item)).filter(Boolean);
    return cleaned.length ? cleaned.join(', ') : '';
  }
  const normalized = normalizeBasicValue(value);
  if (!normalized) return '';
  if (/^none$/i.test(normalized)) return '';
  return normalized;
}

function formatRequestsValue(value) {
  const normalized = normalizeBasicValue(value);
  if (!normalized) return '';
  if (/^not provided$/i.test(normalized)) return '';
  return normalized;
}

function formatDisplayValue(value) {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : '—';
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '—';
  }
  if (typeof value === 'object') {
    if ('label' in value && typeof value.label === 'string' && value.label.trim()) {
      return value.label;
    }
    if ('value' in value && (typeof value.value === 'string' || typeof value.value === 'number')) {
      return String(value.value);
    }
    return '—';
  }
  return String(value);
}

function buildPdfHeader(bucket, groupMode, timezone) {
  const safeLabel = String(bucket?.label || '').trim();
  const { minStart, maxEnd } = computeDateBounds(bucket.bookings);
  const computedRange =
    minStart && maxEnd ? deriveDateRangeLabel(minStart, maxEnd, timezone) : '';
  const monthBounds =
    groupMode === 'month' ? deriveMonthBoundsFromLabel(safeLabel, timezone) : null;
  const labelRange =
    groupMode === 'month' ? deriveRangeFromMonthLabel(safeLabel, timezone) : '';

  if (groupMode === 'week') {
    const [weekPartRaw, rangePartRaw] = safeLabel
      .split('·')
      .map((part) => (part ? part.trim() : ''));
    const weekPart = weekPartRaw || 'Selected Week';
    const subtitle = mergeRangeLabels(rangePartRaw, bucket?.rangeLabel || computedRange);
    return {
      title: `Weekly Bookings Report — ${weekPart}`,
      subtitle,
      documentTitle: `Weekly Bookings Report - ${weekPart}`
    };
  }

  if (groupMode === 'month') {
    let clampedRange = computedRange;
    if (monthBounds?.start && monthBounds?.end && minStart && maxEnd) {
      const clampedStart = minStart < monthBounds.start ? monthBounds.start : minStart;
      const clampedEnd = maxEnd > monthBounds.end ? monthBounds.end : maxEnd;
      clampedRange = deriveDateRangeLabel(clampedStart, clampedEnd, timezone);
    }

    const subtitle =
      labelRange ||
      (bucket?.rangeLabel && bucket.rangeLabel !== safeLabel ? bucket.rangeLabel : '') ||
      clampedRange;
    const displayLabel = safeLabel || 'Selected Month';
    return {
      title: `Bookings Report — ${displayLabel}`,
      subtitle,
      documentTitle: `Bookings Report - ${displayLabel}`
    };
  }

  if (groupMode === 'day') {
    const subtitle = computedRange && computedRange !== safeLabel ? computedRange : '';
    return {
      title: `Bookings Report — ${safeLabel || 'Selected Day'}`,
      subtitle,
      documentTitle: `Bookings Report - ${safeLabel || 'Selected Day'}`
    };
  }

  return {
    title: `Bookings Report — ${safeLabel || 'Selected Period'}`,
    subtitle: computedRange,
    documentTitle: `Bookings Report - ${safeLabel || 'Selected Period'}`
  };
}

function mergeRangeLabels(primary, fallback) {
  if (!primary && fallback) return fallback;
  if (!primary) return '';
  if (!fallback) return primary;
  if (/\d{4}/.test(primary)) return primary;
  const commaMatches = fallback.match(/,/g);
  if (commaMatches && commaMatches.length >= 2) {
    return fallback;
  }
  const commaIndex = fallback.lastIndexOf(',');
  if (commaIndex !== -1) {
    return `${primary.trim()}${fallback.slice(commaIndex)}`;
  }
  return `${primary.trim()} (${fallback})`;
}

function computeDateBounds(bookings) {
  let minStart = null;
  let maxEnd = null;

  bookings.forEach((booking) => {
    const startValue = extractFirstValue(
      [booking],
      ['startTime', 'start_time', 'start', 'start_at', 'startsAt']
    );
    const endValue = extractFirstValue(
      [booking],
      ['endTime', 'end_time', 'end', 'end_at', 'endsAt']
    );
    const startDate = parseDateValue(startValue);
    const endDate = parseDateValue(endValue) || startDate;
    if (startDate && (!minStart || startDate < minStart)) {
      minStart = startDate;
    }
    if (endDate && (!maxEnd || endDate > maxEnd)) {
      maxEnd = endDate;
    }
  });

  return { minStart, maxEnd };
}

function deriveDateRangeLabel(startDate, endDate, timezone) {
  if (!startDate || !endDate) return '';

  const startParts = getDateParts(startDate, timezone);
  const endParts = getDateParts(endDate, timezone);
  const monthDayFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    month: 'short',
    day: 'numeric'
  });
  const monthDayYearFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  if (
    startParts.year === endParts.year &&
    startParts.month === endParts.month &&
    startParts.day === endParts.day
  ) {
    return monthDayYearFormatter.format(startDate);
  }

  if (startParts.year === endParts.year && startParts.month === endParts.month) {
    const startLabel = monthDayFormatter.format(startDate);
    return `${startLabel}–${endParts.day}, ${startParts.year}`;
  }

  if (startParts.year === endParts.year) {
    const startLabel = monthDayFormatter.format(startDate);
    const endLabel = monthDayFormatter.format(endDate);
    return `${startLabel} – ${endLabel}, ${startParts.year}`;
  }

  const startLabel = monthDayYearFormatter.format(startDate);
  const endLabel = monthDayYearFormatter.format(endDate);
  return `${startLabel} – ${endLabel}`;
}

function deriveRangeFromMonthLabel(label, timezone) {
  if (!label) return '';
  const match = label.match(/\b([A-Za-z]+)\s+(\d{4})\b/);
  if (!match) return '';

  const monthName = match[1];
  const year = Number(match[2]);
  const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
  if (Number.isNaN(monthIndex)) return '';

  const startOfMonth = new Date(Date.UTC(year, monthIndex, 1, 12, 0, 0));
  const endOfMonth = new Date(Date.UTC(year, monthIndex + 1, 0, 12, 0, 0));

  return deriveDateRangeLabel(startOfMonth, endOfMonth, timezone);
}

function deriveMonthBoundsFromLabel(label, timezone) {
  if (!label) return null;
  const match = label.match(/\b([A-Za-z]+)\s+(\d{4})\b/);
  if (!match) return null;

  const monthName = match[1];
  const year = Number(match[2]);
  const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
  if (Number.isNaN(monthIndex)) return null;

  return {
    start: new Date(Date.UTC(year, monthIndex, 1, 12, 0, 0)),
    end: new Date(Date.UTC(year, monthIndex + 1, 0, 12, 0, 0))
  };
}

function getDateParts(date, timezone) {
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  })
    .formatToParts(date)
    .forEach((part) => {
      if (part.type === 'year' || part.type === 'month' || part.type === 'day') {
        parts[part.type] = Number(part.value);
      }
    });

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day
  };
}

function buildMapsPlaceLink(addressText) {
  if (!addressText || typeof addressText !== 'string') return '';
  const cleaned = addressText.trim().replace(/\s+/g, ' ');
  if (!cleaned) return '';
  const encoded = encodeURIComponent(cleaned).replace(/%20/g, '+');
  return `https://www.google.com/maps/place/${encoded}`;
}
